import type { IconEnum } from '@ui-elements';

/**
 * What a predefined call to action renders as.
 *
 * An editor picking "Build your own" chooses the destination, the wording and the icon all at once, and none
 * of the three are typed into the backoffice: the site owns them. That is deliberate - the wording is the
 * same on every offer, the icon cannot come from the data at all (see CTA_TEMPLATES below), and a link an
 * editor typed by hand is a link that can 404 on a site the editor does not deploy.
 *
 * A preset CTA is still stored with a `text` and a `link` - a snapshot of what the site would render at the
 * moment it was saved. Nothing here reads it. It exists so that a bundle older than the kind, or one that
 * never learns the kind, still renders a sensible link instead of an empty one: the site FE is released per
 * Octopus deployment while offer data lives in one shared table, so "an older bundle reads newer data" is a
 * standing condition rather than a deploy-window edge case. The same reasoning runs the other way, which is
 * why an unrecognised kind degrades to the stored snapshot rather than being dropped or throwing.
 *
 * Kept as plain functions, with no React and no Recoil, so both renderers of a CTA - the icon row and the
 * primary button - resolve one from the same rules, and so the rules are unit testable on their own.
 */

export type CtaIcon = {
  type: keyof typeof IconEnum;
  size: number;
};

/**
 * The stored CTA content this resolver reads: structurally the CTA branch of `OfferContent`, narrowed to the
 * three keys that decide what is rendered, so a test can state a case in one line and so nothing here can
 * quietly start depending on `sections` or `type` - both of those stay the stored values, always.
 */
export type StoredCtaContent = {
  text: string;
  link: string;
  kind?: string;
  linkOverride?: string;
};

export type ResolvedCta = {
  label: string;
  href: string;
  /**
   * The icon a recognised kind carries, or null for a CTA whose kind is absent or unrecognised - which the
   * caller reads as "decide the icon the way you did before", not as "render no icon". Only the icon row has
   * an icon to fall back to; the button row has none, and ignores this.
   */
  icon: CtaIcon | null;
};

export const CTA_KINDS = [
  'build-your-own',
  'explore-more',
  'download-brochure',
  'book-test-drive',
] as const;

export type CtaKind = (typeof CTA_KINDS)[number];

type CtaTemplate = {
  title: string;
  href: string;
  icon: CtaIcon;
};

/**
 * The presets, closed and hard-coded.
 *
 * The hrefs are the generic destinations, the same on every offer: no model in the path, no `?model=`, no
 * per-model branch. Model-specific pages exist for some cars, but only some, and a little over half of the
 * live offers resolve no single model - so a model-aware href would have to be a rule with an exception,
 * which is how a customer lands on a 404 from an offer page. One destination per kind, probed on the running
 * site, is the whole rule.
 *
 * The icon must come from a table like this one and never from the data. `getIconComponent` in
 * svg-icon/index.tsx ends in a `throw` with no default branch, and a throw during render with no error
 * boundary anywhere near either offer page takes the offer content region down with it - so an icon key that
 * an editor could type, or that a newer backoffice could invent, is a page-blanking vector. `keyof typeof
 * IconEnum` is what makes a typo here a build failure instead.
 *
 * Each icon carries its own em size because this set is not drawn on a common grid: SvgIcon fits the artwork
 * inside a square box, so an icon that fills its viewBox (wrench, brochure) comes out at full height while
 * the car - 487x224 of a 500x500 viewBox - comes out at under half of it. Sizing the car up is what makes
 * the row read as one line of marks; the extra width is the shape being wide, as in the design.
 */
const CTA_TEMPLATES: Record<CtaKind, CtaTemplate> = {
  'build-your-own': {
    title: 'Build your own',
    href: '/configure',
    icon: { type: 'wrench', size: 1.5 },
  },
  'explore-more': {
    title: 'Explore more',
    href: '/showroom',
    icon: { type: 'modelSedanHatch', size: 2.9 },
  },
  'download-brochure': {
    title: 'Download a brochure',
    href: '/brochure-download',
    icon: { type: 'brochure', size: 1.5 },
  },
  // The one preset whose destination is also read outside the CTA row: the panel beside the enquiry form
  // takes its button's href from this kind, so an offer that overrides it moves both. See TestDriveHref.
  'book-test-drive': {
    title: 'Book a test drive',
    href: '/book-test-drive',
    // 1.3, not the 1.5 the wrench and brochure use: this artwork fills 94% of its viewBox against their
    // ~80%, so at 1.5em its ink measures 22.5px beside their 19.5px and 18.9px and the row stops reading
    // as one line of marks. 1.3em puts it at 19.6px - measured in the browser, as the others were.
    icon: { type: 'drive', size: 1.3 },
  },
};

/**
 * Narrows a stored kind to one this build knows about.
 *
 * Membership is tested against the list, not with `kind in CTA_TEMPLATES`: `kind` is data, and `'toString' in
 * CTA_TEMPLATES` is true, so the `in` form would treat every inherited property name as a valid kind and
 * hand back undefined as a template.
 */
export function isCtaKind(kind?: string): kind is CtaKind {
  return !!kind && (CTA_KINDS as readonly string[]).includes(kind);
}

/**
 * What to render for one stored CTA.
 *
 * A recognised kind takes its wording and icon from the table above and ignores what is stored. Its
 * destination comes from the table too, unless an editor typed one - an offer sending "Explore more" at a
 * campaign page rather than the showroom, say. The wording and the icon stay the site's either way: those are
 * what make the row read as one set of links, and an editor who needs different wording wants a Custom CTA.
 *
 * Anything else - no kind at all, which is every CTA authored before presets existed, or a kind from a newer
 * backoffice than this build - renders exactly what is stored.
 */
export function resolveOfferCta(content: StoredCtaContent): ResolvedCta {
  const template = isCtaKind(content.kind)
    ? CTA_TEMPLATES[content.kind]
    : undefined;

  if (!template) {
    return { label: content.text, href: content.link, icon: null };
  }

  const override = content.linkOverride?.trim();

  return {
    label: template.title,
    href: override && override.length > 0 ? override : template.href,
    icon: template.icon,
  };
}

/**
 * The icon for a CTA that carries no kind the site knows: the one for its place in the row, and the chevron
 * the site already uses past the end of the table, so an offer authoring a fourth link does not lose it.
 *
 * This is the whole of what the icon row could do before the presets existed, and it stays because every CTA
 * stored to date is one of these. It is also the behaviour the presets were introduced to escape - the mark
 * belongs to the slot, not to the link, so reordering the row reshuffles the icons and the third link gets a
 * brochure whatever it points at.
 */
export function positionalCtaIcon(index: number): CtaIcon {
  return POSITIONAL_ICONS[index] ?? FALLBACK_ICON;
}

// Matched to the design's three marks: crossed tools, a car in profile, a brochure. `car` is a front view in
// this set, which is why the model profile is used instead, and `brochure` was added to the set for the third
// - see svg-icon/svgs/Brochure.tsx for why pdfIcon could not be used.
const POSITIONAL_ICONS: CtaIcon[] = [
  { type: 'wrench', size: 1.5 },
  { type: 'modelSedanHatch', size: 2.9 },
  { type: 'brochure', size: 1.5 },
];

const FALLBACK_ICON: CtaIcon = { type: 'chevronRight', size: 1.2 };
